import { Skeleton } from '@mantine/core'
import { ResponsiveBar } from '@nivo/bar'
import type { BarDatum, BarLayer } from '@nivo/bar'

import { chartTheme, formatMonth } from './chartTheme'
import { TotalLabelLayer } from './TotalLabelLayer'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditTrailActivityBucket {
  period: string
  email: number
  call: number
  fax: number
  ats: number
  total: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BAR_KEYS = ['email', 'call', 'fax', 'ats'] as const
const BAR_COLORS: Record<string, string> = {
  email: '#77BEE7', // brand-300
  call: '#47CD89',  // success-400
  fax: '#FEC84B',   // warning-300
  ats: '#FDA29B',   // error-300
}
const BAR_LABELS: Record<string, string> = {
  email: 'Email',
  call: 'Phone',
  fax: 'Fax',
  ats: 'ATS',
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

interface TooltipProps {
  id: string | number
  value: number
  indexValue: string | number
  data: BarDatum
  color: string
}

const BarTooltip = ({ id, indexValue, data }: TooltipProps) => {
  return (
    <div className="rounded-lg bg-white px-3 py-2 shadow-md ring-1 ring-gray-200">
      <p className="mb-1.5 text-xs font-medium text-gray-500">
        {formatMonth(String(indexValue))}
      </p>
      <div className="flex flex-col gap-1">
        {BAR_KEYS.map((key) => {
          const count = (data[key] as number) ?? 0
          if (count === 0) return null
          return (
            <div
              key={key}
              className={`flex items-center justify-between gap-4 ${
                String(id) === key ? 'font-semibold' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: BAR_COLORS[key] ?? '#94a3b8' }}
                />
                <span className="text-xs text-gray-700">
                  {BAR_LABELS[key] ?? key}
                </span>
              </div>
              <span className="text-xs text-gray-700">{count}</span>
            </div>
          )
        })}
      </div>
      <p className="mt-1.5 flex justify-between border-t border-gray-100 pt-1.5 text-xs font-semibold text-gray-700">
        <span>Total</span>
        <span>{(data.total as number) ?? 0}</span>
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoEAuditTrailActivityChartProps {
  data: AuditTrailActivityBucket[]
  loading?: boolean
}

export const VoEAuditTrailActivityChart = ({
  data,
  loading = false,
}: VoEAuditTrailActivityChartProps) => {
  if (loading) {
    return <Skeleton height={350} />
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div className="h-[300px]">
      <ResponsiveBar
        data={data}
        keys={[...BAR_KEYS]}
        indexBy="period"
        groupMode="stacked"
        margin={{ top: 30, right: 20, bottom: 60, left: 50 }}
        padding={0.35}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={(bar) => BAR_COLORS[bar.id] ?? '#94a3b8'}
        enableLabel={false}
        enableGridY={false}
        axisBottom={{
          tickSize: 0,
          tickPadding: 8,
          format: formatMonth,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 5,
        }}
        theme={chartTheme}
        tooltip={BarTooltip}
        legendLabel={(datum) =>
          BAR_LABELS[String(datum.id)] ?? String(datum.id)
        }
        legends={[
          {
            dataFrom: 'keys',
            anchor: 'bottom',
            direction: 'row',
            translateY: 50,
            itemWidth: 80,
            itemHeight: 20,
            itemTextColor: '#6b7280',
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ]}
        layers={[
          'grid',
          'axes',
          'bars',
          TotalLabelLayer as BarLayer<BarDatum>,
          'markers',
          'legends',
          'annotations',
        ]}
      />
    </div>
  )
}
